import { IframeMessage } from '@/constants/iframeMessages';
import { iframeMessenger, type EventData } from '@/services/iframeMessenger';
import { frontendEventsService } from '@/services/frontendEventsService';
import { getEventDataForSdkEvents } from './eventDataBuilder';
import type { CheckoutData } from '@/types/checkout';
import type { PaymentMethodEnum } from '@/types/common';

/**
 * Emits a checkout lifecycle event on both channels:
 *   1. `postMessage` to the parent (publisher SDK contract)
 *   2. authenticated POST to events-gateway via frontend-events-sdk
 *
 * `track` is scheduled in `finally` so a postMessage failure still ships
 * the data-lake event. `track` itself never throws / never blocks.
 */
const emitSdkEvent = (message: IframeMessage, eventData: EventData): void => {
  try {
    iframeMessenger.postMessage(message, eventData);
  } finally {
    frontendEventsService.track(message, eventData);
  }
};

export const notifyOrderSuccess = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.ORDER_COMPLETED_SUCCESS, eventData);
};

export const notifyOrderFailed = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.ORDER_COMPLETED_FAILED, eventData);
};

export const notifyCloseCheckout = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.CLOSE_CHECKOUT, eventData);
};

export const notifyCheckoutOpened = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.CHECKOUT_OPENED, eventData);
};

export const notifyPaymentIntent = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.ON_PAYMENT_INTENT, eventData);
};

/**
 * postMessage-only. Publishers use this to see the first tap separately from
 * payment intent. There is no events-gateway DTO for it; `PAYMENT_INTENT`
 * stage `started` stays on `notifyPaymentIntent`.
 */
export const notifyPaymentButtonClicked = (eventData: EventData) => {
  iframeMessenger.postMessage(IframeMessage.ON_PAYMENT_BUTTON_CLICKED, eventData);
};

export const notifyPaymentIntentSuccess = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.PAYMENT_INTENT_SUCCESS, eventData);
};

export const notifyPaymentIntentFailed = (
  checkoutData: CheckoutData,
  paymentMethod?: PaymentMethodEnum,
  additionalProperties?: Partial<EventData>
) => {
  const eventData = getEventDataForSdkEvents(checkoutData, {
    paymentMethodName: paymentMethod,
    ...additionalProperties,
  });
  emitSdkEvent(IframeMessage.PAYMENT_INTENT_FAILED, eventData);
};

export const notifyOrderCreated = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.ORDER_CREATED, eventData);
};

export const notifyPaymentMethodSelected = (eventData: EventData) => {
  emitSdkEvent(IframeMessage.PAYMENT_METHOD_SELECTED, eventData);
};

/**
 * postMessage-only, unlike every helper above: the height ping is a layout
 * concern for the embedding host, not a checkout lifecycle event, so it has no
 * `@appcharge/frontend-events` DTO (`toFrontendCheckoutEvent` returns `null`
 * for it) and would otherwise just flood the events queue on every resize.
 */
export const notifyCheckoutDimensionsChanged = (height: number) => {
  iframeMessenger.postDimensions({ height });
};
